import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import type { UsuarioSessao } from '@crm/shared'
import { usePosts } from '../api/hooks-marketing'
import {
  IconeBatePapo,
  IconeCalendario,
  IconeConfiguracao,
  IconeEstatisticas,
  IconeImagem,
  IconeLeads,
  IconePainel,
} from '../componentes/icones'
import { Marca, type Produto } from '../componentes/Marca'
import { juntar } from '../componentes/ui'
import { pode, rotaInicialDaAdministracao, rotaInicialDoCrm, rotaInicialDoMarketing } from '../permissao'

/**
 * A escolha entre os produtos da casa (ADR 0057 e 0059), aberta pela logo do
 * topo: o CRM (atendimento e vendas), o FaroEscolar Marketing (redes sociais)
 * e a FaroEscolar Administração (painel, estatísticas, equipe e sistema).
 *
 * Mostra só o que a pessoa pode abrir. Quem tem um produto só vê um cartão,
 * e a escolha continua dizendo onde ela está e o que existe ali.
 */
export function EscolhaDeProduto({ usuario }: { usuario: UsuarioSessao }) {
  const crm = rotaInicialDoCrm(usuario)
  const marketing = rotaInicialDoMarketing(usuario)
  const administracao = rotaInicialDaAdministracao(usuario)
  const quantos = [crm, marketing, administracao].filter(Boolean).length

  return (
    <div
      className={juntar(
        'mx-auto flex min-h-full flex-col justify-center py-8',
        quantos > 2 ? 'max-w-6xl' : 'max-w-4xl',
      )}
    >
      {/*
        Sem o nome da pessoa (18/10/2026, ADR 0072): quem chega aqui já foi
        cumprimentado na tela de entrada, e a escolha abre várias vezes no dia.
      */}
      <h1 className="text-center text-3xl font-bold text-tinta-900">Onde vamos trabalhar agora?</h1>
      <p className="mt-2 text-center text-tinta-500">
        A logo no alto da tela sempre traz você de volta para esta escolha.
      </p>

      <div className={juntar('mt-10 grid gap-5 md:grid-cols-2', quantos > 2 && 'lg:grid-cols-3')}>
        {crm ? (
          <CartaoDoProduto
            produto="CRM"
            para={crm}
            descricao="Atendimento por WhatsApp, leads, contatos, empresas e o calendário da equipe."
            itens={[
              { Icone: IconeBatePapo, texto: 'Bate-papo com as famílias' },
              { Icone: IconeLeads, texto: 'Funil de matrículas' },
              { Icone: IconeCalendario, texto: 'Tarefas e retornos' },
            ]}
            botao="Abrir o CRM"
          />
        ) : null}
        {marketing ? (
          <CartaoDoProduto
            produto="Marketing"
            para={marketing}
            descricao="Posts do Instagram, Facebook, LinkedIn e das outras redes, e os leads que chegam pelos anúncios."
            itens={[
              { Icone: IconeImagem, texto: 'Post com prévia de cada rede' },
              { Icone: IconeCalendario, texto: 'Calendário de publicações' },
              { Icone: IconeLeads, texto: 'Aprovação antes de sair' },
            ]}
            botao="Abrir o Marketing"
            rodape={pode(usuario, 'marketing.ver') ? <ResumoDoMarketing /> : undefined}
          />
        ) : null}
        {administracao ? (
          <CartaoDoProduto
            produto="Administração"
            para={administracao}
            descricao="O painel do admin, as estatísticas, a equipe e as regras do sistema."
            itens={[
              { Icone: IconePainel, texto: 'Painel do admin' },
              { Icone: IconeEstatisticas, texto: 'Estatísticas e metas' },
              { Icone: IconeConfiguracao, texto: 'Usuários, regras, avisos e logs' },
            ]}
            botao="Abrir a Administração"
          />
        ) : null}
      </div>

      {!crm && !marketing && !administracao ? (
        <p className="mt-8 text-center text-sm text-tinta-500">
          Seu usuário ainda não abre nenhuma parte do sistema. Fale com quem administra.
        </p>
      ) : null}
    </div>
  )
}

/** A cor de cada produto: a mesma do selo da marca, na faixa do alto e nos ícones da lista. */
const TOM_DO_PRODUTO: Record<Produto, { faixa: string; icone: string }> = {
  CRM: { faixa: 'from-marca via-faro-turquesa to-faro-verde', icone: 'bg-marca/10 text-marca' },
  Marketing: {
    faixa: 'from-faro-verde via-faro-turquesa to-marca',
    icone: 'bg-faro-verde/10 text-faro-verde',
  },
  Administração: {
    faixa: 'from-faro-ambar via-faro-turquesa to-marca',
    icone: 'bg-faro-ambar/15 text-amber-700 dark:text-faro-ambar',
  },
}

function CartaoDoProduto({
  produto,
  para,
  descricao,
  itens,
  botao,
  rodape,
}: {
  produto: Produto
  para: string
  descricao: string
  itens: { Icone: (p: { className?: string }) => ReactNode; texto: string }[]
  botao: string
  rodape?: ReactNode
}) {
  return (
    <Link
      to={para}
      className="group relative flex flex-col overflow-hidden rounded-3xl bg-superficie p-7 shadow-suave ring-1 ring-tinta-200/60 outline-none transition hover:-translate-y-0.5 hover:shadow-alta focus-visible:ring-2 focus-visible:ring-marca/60"
    >
      <div
        aria-hidden
        className={juntar('absolute inset-x-0 top-0 h-1.5 bg-linear-to-r', TOM_DO_PRODUTO[produto].faixa)}
      />
      {/*
        O selo embaixo do nome: ao lado, "ADMINISTRAÇÃO" passava da largura do
        cartão em três colunas e saía cortado pela borda.
      */}
      <Marca tamanho="m" produto={produto} seloEmbaixo />
      <p className="mt-5 text-sm leading-relaxed text-tinta-600">{descricao}</p>
      <ul className="mt-5 space-y-2.5">
        {itens.map(({ Icone, texto }) => (
          <li key={texto} className="flex items-center gap-2.5 text-sm text-tinta-700">
            <span
              className={juntar(
                'inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full',
                TOM_DO_PRODUTO[produto].icone,
              )}
            >
              <Icone className="h-4 w-4" />
            </span>
            {texto}
          </li>
        ))}
      </ul>
      {rodape ? <div className="mt-5">{rodape}</div> : null}
      <span aria-hidden className="min-h-6 flex-1" />
      <span className="mt-auto inline-flex w-fit items-center gap-1.5 rounded-full bg-marca px-5 py-2.5 text-sm font-semibold text-white shadow-media shadow-marca/25 transition group-hover:bg-marca-escura">
        {botao}
        <span aria-hidden>→</span>
      </span>
    </Link>
  )
}

/** O que espera alguém no Marketing: o que pede aprovação e o que está na hora. */
function ResumoDoMarketing() {
  const { data: paraAprovar } = usePosts({ status: 'em_aprovacao' })
  const { data: naHora } = usePosts({ status: 'na_hora' })
  const aprovar = paraAprovar?.length ?? 0
  const hora = naHora?.length ?? 0
  if (aprovar === 0 && hora === 0) return null
  return (
    <p className="rounded-xl bg-amber-50 px-3 py-2 text-xs font-medium text-amber-900">
      {[
        hora > 0 ? `${hora} ${hora === 1 ? 'post na hora' : 'posts na hora'} de publicar` : null,
        aprovar > 0 ? `${aprovar} para aprovar` : null,
      ]
        .filter(Boolean)
        .join(' · ')}
    </p>
  )
}
